import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Check } from 'lucide-react';

const ServiceAgreementDialog = ({ bookingId, isOpen, onClose, onAccepted, userRole = 'customer' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreementText, setAgreementText] = useState('');
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen) {
      fetchAgreementText();
    }
  }, [isOpen]);

  const fetchAgreementText = async () => {
    setLoading(true);
    try {
      const agreementType = userRole === 'vendor' ? 'vendor_participation' : 'service_agreement';
      const response = await fetch(`${API_URL}/api/agreements/${agreementType}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch agreement');
      }
      
      const data = await response.json();
      setAgreementText(data.content);
      setError('');
    } catch (err) {
      console.error('Failed to fetch agreement:', err);
      setError('Failed to load agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/accept-agreement`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accepted: true })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to accept agreement';
        try {
          const errData = await response.json();
          errorMessage = errData.detail || errorMessage;
        } catch (jsonErr) {
          // If JSON parsing fails, use the response status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Success - parse the response if needed
      try {
        await response.json();
      } catch (jsonErr) {
        // Some endpoints might not return JSON, that's okay
      }

      onAccepted();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Service Agreement</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Please review and accept the service agreement before proceeding
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div
            onScroll={handleAgreementScroll}
            className="border border-gray-300 rounded-lg p-4 h-96 overflow-y-auto bg-gray-50 text-sm whitespace-pre-wrap"
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading agreement...</div>
              </div>
            ) : agreementText ? (
              agreementText
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No agreement text available
              </div>
            )}
          </div>

          {!scrolledToBottom && (
            <p className="text-sm text-amber-600 flex items-center mt-4">
              <AlertCircle size={16} className="mr-1" />
              Please scroll to the bottom to continue
            </p>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm mt-4 flex items-start">
              <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6 space-y-3">
          <button
            onClick={handleAccept}
            disabled={!scrolledToBottom || loading || !agreementText}
            className="w-full bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? 'Processing...' : (
              <>
                <Check className="mr-2" size={20} />
                I Accept the Agreement
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceAgreementDialog;